import Hls from 'hls.js';
import Player from 'video.js/dist/types/player';

/**
 * Returns true when `time` falls inside any MSE buffered range.
 * Uses hls.js `maxBufferHole` so tiny gaps between ranges do not trigger a reload.
 */
function isTimeBuffered(media: HTMLMediaElement, time: number, maxBufferHole: number): boolean {
  for (let i = 0; i < media.buffered.length; i++) {
    const start = media.buffered.start(i);
    const end = media.buffered.end(i);
    if (time + maxBufferHole >= start && time - maxBufferHole <= end) {
      return true;
    }
  }
  return false;
}

/**
 * Fixes out-of-buffer scrubbing for VOD HLS playlists with large (e.g. 30s) segments.
 *
 * ## Problem
 *
 * After the first fragment plays, hls.js sets `hasEnoughToStart`. On subsequent seeks
 * **outside** the buffered range, its internal `onMediaSeeking` handler does **not**
 * reset `nextLoadPosition` to the new playhead — it only does that before playback
 * has started. The stream controller keeps loading from the old cursor (often the end
 * of the previous buffer), which can fetch segments *before* the seek target (sn-1,
 * sn-2, …) instead of jumping straight to the fragment that contains the seek time.
 *
 * ## Fix
 *
 * On `seeking`, when the target time is not buffered, abort in-flight loads and call
 * `hls.startLoad(seekTime)` so the next `FRAG_LOADING` targets the correct fragment.
 *
 * ## What this does not change
 *
 * hls.js still downloads each `.ts` segment from its **start** (playlist `start` time),
 * not from the exact seek timestamp within the segment. Seeking to 26:56 in a 26:30–
 * 26:59 segment will still download from ~26:30; that is normal HLS/TS behaviour.
 *
 * Seeks that land inside an existing buffered range are left alone so hls.js can reuse
 * decoded data without restarting the loader.
 *
 * @returns Teardown function that removes the `seeking` listener.
 */
export function setupHlsVodSeekJump(hls: Hls, player: Player): () => void {
  const media = player.tech(false).el() as HTMLMediaElement;
  const maxBufferHole = hls.config.maxBufferHole;

  const onSeeking = () => {
    const seekTime = media.currentTime;

    // In-buffer seek: nothing to do; hls.js can play from existing MSE ranges.
    if (!Number.isFinite(seekTime) || isTimeBuffered(media, seekTime, maxBufferHole)) {
      return;
    }

    // Out-of-buffer seek: reset the load cursor so we fetch the target fragment only.
    hls.stopLoad();
    hls.startLoad(seekTime);
  };

  media.addEventListener('seeking', onSeeking);

  return () => {
    media.removeEventListener('seeking', onSeeking);
  };
}
